import { z } from 'zod';
import type { components } from './schema.js';
import { montarCaminho, requisitar } from './client.js';
import { analisar } from './validacao.js';

/**
 * Identificação no armazém: o que o QR da etiqueta carrega e como o coletor
 * conta no inventário — pelo código do PRODUTO ou pelo código do LOTE.
 *
 * `GET` para Administrador e Supervisor, `PUT` só para o Administrador. A API
 * recusa `LOTE` com 422 `LOTES_NAO_SINCRONIZADOS` enquanto o tenant não tiver
 * nenhum lote ativo sincronizado do ERP.

 */

const CAMINHO = montarCaminho('/configuracoes/identificacao');

export const MODOS_DE_IDENTIFICACAO = ['PRODUTO', 'LOTE'] as const;

export type ModoDeIdentificacao = components['schemas']['ModoIdentificacaoQr'];

export const configuracaoDeIdentificacaoSchema = z.object({
  modoIdentificacaoQr: z.enum(MODOS_DE_IDENTIFICACAO),
  /** Há ao menos um lote ativo sincronizado do ERP — sem isso, `LOTE` é recusado. */
  lotesSincronizados: z.boolean(),
  /** `null` enquanto ninguém mudou: vale o padrão, código do produto. */
  alteradoEm: z.string().nullable(),
}) satisfies z.ZodType<components['schemas']['ConfiguracaoIdentificacaoResponse']>;

export type ConfiguracaoDeIdentificacao = z.infer<typeof configuracaoDeIdentificacaoSchema>;

export async function obterIdentificacao(): Promise<ConfiguracaoDeIdentificacao> {
  return analisar(configuracaoDeIdentificacaoSchema, await requisitar(CAMINHO), CAMINHO);
}

/**
 * Troca o modo do tenant.
 *
 * @throws {ErroApi} `LOTES_NAO_SINCRONIZADOS` (422) ao pedir `LOTE` sem lote
 * nenhum sincronizado.
 */
export async function definirModoDeIdentificacao(
  modoIdentificacaoQr: ModoDeIdentificacao,
): Promise<ConfiguracaoDeIdentificacao> {
  const corpo = await requisitar(CAMINHO, { metodo: 'PUT', corpo: { modoIdentificacaoQr } });
  return analisar(configuracaoDeIdentificacaoSchema, corpo, CAMINHO);
}
